import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Divider,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Security as SecurityIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import RoleService from '../../services/role.service';
import RoleModuleAccessService from '../../services/role-module-access.service';
import systemModuleService, { SystemModule } from '../../services/system-module.service';

const RoleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSystemRole, setIsSystemRole] = useState(false);
  const [availableModules, setAvailableModules] = useState<SystemModule[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    type: '',
  });

  useEffect(() => {
    fetchAvailableModules();
    if (isEdit && id) {
      fetchRole(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchAvailableModules = async () => {
    try {
      const response = await systemModuleService.getAllModules();
      const modules = response.data || [];
      // Filter out dashboard and settings as they're always available
      const filteredModules = modules.filter(
        (m: SystemModule) => m.isActive && m.moduleId !== 'dashboard' && m.moduleId !== 'settings'
      );
      setAvailableModules(filteredModules);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchRole = async (roleId: number) => {
    try {
      const response = await RoleService.getOne(roleId);
      const role = response.data?.role;
      if (role) {
        setFormData({
          name: role.name,
          description: role.description || '',
          type: role.type,
        });
        // Check if it's a system role
        if (role.type === 'authenticated' || role.type === 'public') {
          setIsSystemRole(true);
        }

        // Fetch role modules
        const modulesResponse = await RoleModuleAccessService.getByRole(roleId);
        const roleModules = modulesResponse.data?.data || [];
        setSelectedModules(roleModules.map((m: any) => m.moduleId));
      }
    } catch (error) {
      console.error('Error fetching role:', error);
      setError('Failed to load role');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleModuleToggle = (moduleId: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const generateTypeFromName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData: any = {
        name: formData.name,
        description: formData.description,
        type: formData.type || generateTypeFromName(formData.name),
      };

      let roleId: number;

      if (isEdit && id) {
        await RoleService.update(parseInt(id), submitData);
        roleId = parseInt(id);
      } else {
        const createResponse = await RoleService.create(submitData);
        roleId = createResponse.data?.role?.id;
      }

      // Update module access
      if (roleId) {
        await RoleModuleAccessService.setRoleModules(roleId, selectedModules);
      }

      navigate('/roles');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <SecurityIcon fontSize="large" color="primary" />
        <Typography variant="h4">
          {isEdit ? 'Edit Role' : 'Create Role'}
        </Typography>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Paper sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {isSystemRole && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>System Role:</strong> This is a system role. You can modify the name and description, but be careful as it affects core authentication.
              </Alert>
            )}

            <Stack spacing={3}>
              <Typography variant="h6" color="primary">
                Role Information
              </Typography>

              <TextField
                fullWidth
                required
                label="Role Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                helperText="A clear, descriptive name for this role (e.g., HR Manager, Sales Representative)"
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                helperText="Describe what this role can do and who should have it"
              />

              <TextField
                fullWidth
                label="Role Type"
                name="type"
                value={formData.type || generateTypeFromName(formData.name)}
                onChange={handleChange}
                disabled={isEdit}
                helperText={
                  isEdit
                    ? 'Role type cannot be changed after creation'
                    : 'Auto-generated from name, or enter a custom value (lowercase, no spaces)'
                }
              />
            </Stack>
          </Paper>

          {/* Module Access */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Module Access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select which modules this role can access. Dashboard and Settings are always available to all users.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {availableModules.map((module) => (
                <Box key={module.id} sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '250px' }}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedModules.includes(module.moduleId) ? '2px solid' : '1px solid',
                      borderColor: selectedModules.includes(module.moduleId) ? 'primary.main' : 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1,
                      }
                    }}
                    onClick={() => handleModuleToggle(module.moduleId)}
                  >
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedModules.includes(module.moduleId)}
                            onChange={() => handleModuleToggle(module.moduleId)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {module.name}
                            </Typography>
                            {module.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {module.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

            {availableModules.length === 0 && (
              <Alert severity="info">
                No modules available. Please activate modules in Module Management first.
              </Alert>
            )}
          </Paper>

          {/* Permissions Info */}
          <Paper sx={{ p: 3 }}>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                <strong>About Permissions:</strong>
              </Typography>
              <Typography variant="body2">
                After {isEdit ? 'saving, use the "Manage Permissions" button to' : 'creating this role, you can'} configure detailed permissions. Module access controls which sections users can see, while permissions control what they can do within those sections.
              </Typography>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>Enable/disable specific API endpoints</li>
                <li>Control CRUD operations (Create, Read, Update, Delete)</li>
                <li>Set granular access permissions for each module</li>
              </ul>
            </Alert>
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Box>
                {isEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<VpnKeyIcon />}
                    onClick={() => navigate(`/roles/${id}/permissions`)}
                    color="primary"
                  >
                    Manage Permissions
                  </Button>
                )}
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/roles')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Role'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </form>

      {isEdit && !loading && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Additional Information
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Role ID
              </Typography>
              <Chip label={id} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Role Type
              </Typography>
              <Chip label={formData.type} color="primary" />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selected Modules
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label="Dashboard" color="default" size="small" />
                <Chip label="Settings" color="default" size="small" />
                {selectedModules.map((moduleId) => {
                  const module = availableModules.find(m => m.moduleId === moduleId);
                  return (
                    <Chip 
                      key={moduleId} 
                      label={module?.name || moduleId} 
                      color="primary" 
                      size="small" 
                    />
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default RoleForm;
